/* VYRD art lightbox — images only, scoped to the main product gallery. */
(() => {
  if (window.VyrdProductLightbox) return;

  class VyrdProductLightbox {
    constructor() {
      this.images = [];
      this.index = 0;
      this.lastFocusedElement = null;
      this.previousBodyOverflow = '';
      this.touchStart = null;

      this.createLightbox();
      this.bindEvents();
    }

    createLightbox() {
      this.element = document.createElement('div');
      this.element.id = 'vyrd-product-lightbox';
      this.element.dataset.imageCount = '1';
      this.element.setAttribute('aria-hidden', 'true');
      this.element.innerHTML = `
        <div class="vyrd-product-lightbox__backdrop" data-vyrd-lightbox-close></div>
        <section class="vyrd-product-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Produktbild vergrößert" tabindex="-1">
          <button class="vyrd-product-lightbox__button vyrd-product-lightbox__close" type="button" aria-label="Bildansicht schließen" data-vyrd-lightbox-close>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6 18 18M18 6 6 18" /></svg>
          </button>
          <button class="vyrd-product-lightbox__button vyrd-product-lightbox__previous" type="button" aria-label="Vorheriges Bild" data-vyrd-lightbox-previous>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
          </button>
          <figure class="vyrd-product-lightbox__stage">
            <img class="vyrd-product-lightbox__image" alt="" decoding="async">
          </figure>
          <button class="vyrd-product-lightbox__button vyrd-product-lightbox__next" type="button" aria-label="Nächstes Bild" data-vyrd-lightbox-next>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
          </button>
          <span class="vyrd-product-lightbox__counter" aria-live="polite"></span>
        </section>
      `;

      document.body.append(this.element);
      this.dialog = this.element.querySelector('.vyrd-product-lightbox__dialog');
      this.image = this.element.querySelector('.vyrd-product-lightbox__image');
      this.counter = this.element.querySelector('.vyrd-product-lightbox__counter');
      this.previousButton = this.element.querySelector('[data-vyrd-lightbox-previous]');
      this.nextButton = this.element.querySelector('[data-vyrd-lightbox-next]');
      this.stage = this.element.querySelector('.vyrd-product-lightbox__stage');
    }

    bindEvents() {
      document.addEventListener('click', event => {
        const trigger = event.target.closest('product-page product-image-zoom[data-image]');
        if (!trigger) return;

        const gallery = trigger.closest('[data-js-product-gallery]');
        if (!gallery) return;

        event.preventDefault();
        this.openFromGallery(gallery, trigger);
      });

      this.element.addEventListener('click', event => {
        if (event.target.closest('[data-vyrd-lightbox-close]')) {
          this.close();
          return;
        }
        if (event.target.closest('[data-vyrd-lightbox-previous]')) {
          this.goTo(this.index - 1);
          return;
        }
        if (event.target.closest('[data-vyrd-lightbox-next]')) {
          this.goTo(this.index + 1);
          return;
        }

        if (event.target !== this.image) this.close();
      });

      document.addEventListener('keydown', event => {
        if (!this.isOpen()) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        } else if (event.key === 'Tab') {
          this.trapFocus(event);
        }
      });

      this.stage.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse') return;
        this.touchStart = { x: event.clientX, y: event.clientY };
      });

      this.stage.addEventListener('pointerup', event => {
        if (!this.touchStart || event.pointerType === 'mouse') return;

        const horizontalDistance = event.clientX - this.touchStart.x;
        const verticalDistance = event.clientY - this.touchStart.y;
        this.touchStart = null;

        if (Math.abs(horizontalDistance) < 50 || Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) return;
        this.goTo(horizontalDistance < 0 ? this.index + 1 : this.index - 1);
      });

      this.stage.addEventListener('pointercancel', () => {
        this.touchStart = null;
      });
    }

    openFromGallery(gallery, trigger) {
      this.images = Array.from(gallery.querySelectorAll('product-image-zoom[data-image]')).map(button => {
        const galleryItem = button.closest('.product-gallery-item');
        const galleryImage = galleryItem ? galleryItem.querySelector('img') : null;
        return {
          source: button.dataset.image,
          alt: galleryImage ? galleryImage.alt : ''
        };
      }).filter(image => image.source);

      const selectedIndex = Array.from(gallery.querySelectorAll('product-image-zoom[data-image]')).indexOf(trigger);
      this.index = Math.max(0, selectedIndex);
      this.lastFocusedElement = document.activeElement;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      this.element.classList.add('is-open');
      this.element.setAttribute('aria-hidden', 'false');
      this.showImage();
      this.element.querySelector('[data-vyrd-lightbox-close]').focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen()) return;

      this.element.classList.remove('is-open');
      this.element.setAttribute('aria-hidden', 'true');
      this.image.removeAttribute('src');
      document.body.style.overflow = this.previousBodyOverflow;

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    goTo(nextIndex) {
      if (nextIndex < 0 || nextIndex >= this.images.length) return;
      this.index = nextIndex;
      this.showImage();
    }

    showImage() {
      const currentImage = this.images[this.index];
      if (!currentImage) return;

      this.image.alt = currentImage.alt || '';
      this.image.src = currentImage.source;
      this.element.dataset.imageCount = String(this.images.length);
      this.counter.textContent = `${this.index + 1} / ${this.images.length}`;
      this.previousButton.disabled = this.index === 0;
      this.nextButton.disabled = this.index === this.images.length - 1;
    }

    trapFocus(event) {
      const focusable = Array.from(this.element.querySelectorAll('button:not([disabled])'));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    isOpen() {
      return this.element.classList.contains('is-open');
    }
  }

  window.VyrdProductLightbox = new VyrdProductLightbox();
})();
